'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { SIDEBAR_FULL_SCREEN_WIDTH, SIDEBAR_SMALL_SCREEN_WIDTH } from '@/consts/const';
import { useLocalStorage } from '@/hooks/useLocalStorage';

interface ContentDimensionContextType {
  isFullWidth: boolean;
  setIsFullWidth: (value: boolean) => void;
  isMobile: boolean;
  sidebarWidth: number;
}

const ContentDimensionContext = createContext<ContentDimensionContextType>({
  isFullWidth: true,
  setIsFullWidth: () => {},
  isMobile: false,
  sidebarWidth: SIDEBAR_FULL_SCREEN_WIDTH,
});

export const useContentDimension = () => useContext(ContentDimensionContext);

export default function ContentDimensionProvider({ children }: { children: React.ReactNode }) {
  const [isFullWidth, setIsFullWidth] = useLocalStorage('sidebar_full_width', true);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const getBooleanValue = (value: string | boolean) => {
    if (typeof value === 'boolean') {
      return value;
    }

    return value === 'true';
  };
  
  const sidebarWidth = getBooleanValue(isFullWidth) ? SIDEBAR_FULL_SCREEN_WIDTH : SIDEBAR_SMALL_SCREEN_WIDTH;

  const value = {
    isFullWidth: getBooleanValue(isFullWidth),
    setIsFullWidth,
    isMobile,
    sidebarWidth,
  };

  return <ContentDimensionContext.Provider value={value}>{children}</ContentDimensionContext.Provider>;
}